/**
 * @group Utils

@summary
<blockquote>Handles HTTP error responses by parsing the response body and extracting application-level error details.</blockquote>

@example
```typescript
const errorResult = await handleHttpError(response);
if (errorResult.isErr) {
  console.error('Error:', errorResult.getFail().errorMessage);
}
```
 * @internal
 */
import { AppError, AsyncResult, tryCatchAsync, err } from '@shared/utils';

/**
 * Handles HTTP error responses by attempting to parse application-level error details
 * from the response body, with fallback to HTTP status information.
 * 
 * @param response - The failed HTTP response (response.ok === false)
 * @returns AsyncResult containing the parsed error or HTTP status error
 */
export async function handleHttpError(response: Response): AsyncResult<AppError, AppError> {
  const result = await tryCatchAsync(async () => {
    const contentType = response.headers.get('content-type');
    let errorData: any = {};
    
    // Parse response body based on content type
    if (contentType && contentType.includes('application/json')) {
      errorData = await response.json();
    } else {
      const text = await response.text();
      errorData = { errorMessage: text };
    }
    
    // Check if the response contains a structured error object
    if (errorData?.error?.errorCode && errorData?.error?.errorMessage) {
      return {
        errorCode: errorData.error.errorCode,
        errorMessage: errorData.error.errorMessage
      };
    }
    
    // Fallback to HTTP status error
    return {
      errorCode: 'HTTP_ERROR',
      errorMessage: `HTTP ${response.status}: ${response.statusText}`
    };
  });

  return result.match({
    ok: (error: AppError) => err(error),
    err: (parseError: AppError) => err({
      errorCode: 'HTTP_ERROR',
      errorMessage: `HTTP ${response.status}: ${response.statusText}`
    })
  });
}
